import { useEffect, useState } from 'react';
import { Plus, Trash2, Tag } from 'lucide-react';
import {
  Button,
  IconButton,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Section, ActionBar } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useSetBucketTags, s3ExtractError, type BucketSettings, type BucketTag } from '@/api/s3';

interface TabProps {
  serviceId: string;
  bucket: string;
  data?: BucketSettings;
}

type TagRow = { key: string; value: string };

function toRows(tags?: BucketTag[] | null): TagRow[] {
  return (tags ?? []).map((t) => ({ key: t.key, value: t.value }));
}

/** Key/value tag editor — sends the full tag map on save. */
export function BucketTagsTab({ serviceId, bucket, data }: TabProps) {
  const toast = useToast();
  const save = useSetBucketTags(serviceId, bucket);
  const [rows, setRows] = useState<TagRow[]>(() => toRows(data?.tags));

  // Re-seed when the loaded settings change (e.g. after a refetch).
  useEffect(() => setRows(toRows(data?.tags)), [data?.tags]);

  const update = (i: number, patch: Partial<TagRow>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const onSave = () => {
    const map: Record<string, string> = {};
    for (const r of rows) {
      const k = r.key.trim();
      if (k) map[k] = r.value;
    }
    save.mutate(map, {
      onSuccess: () => toast.success('Tags saved'),
      onError: (e) => toast.error(s3ExtractError(e, 'Failed to save tags')),
    });
  };

  return (
    <Section
      title="Tags"
      description="Key/value tags for this bucket. Saving replaces the full set."
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader
            actions={
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={() => setRows((prev) => [...prev, { key: '', value: '' }])}
              >
                Add tag
              </Button>
            }
          >
            <CardTitle>Bucket tags</CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {rows.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<Tag size={20} />}
                title="No tags yet"
                description='Click "Add tag" to attach a key/value tag to this bucket.'
              />
            ) : (
              rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                  <Input placeholder="Key" value={r.key} onChange={(e) => update(i, { key: e.target.value })} />
                  <Input placeholder="Value" value={r.value} onChange={(e) => update(i, { value: e.target.value })} />
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="Remove tag"
                    onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </div>
              ))
            )}
          </CardBody>
        </Card>

        <ActionBar
          end={
            <Button variant="primary" size="sm" disabled={save.isPending} onClick={onSave}>
              {save.isPending ? 'Saving…' : 'Save tags'}
            </Button>
          }
        />
      </div>
    </Section>
  );
}
